// Team3.js - Contains functions for creating an alternative modern team section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a modern Team section with team members in a visually distinct layout
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the team section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The team section elements
 */
export const createTeam3Elements = (
  getHierarchyName,
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const teamSectionClass = 'team3-section';
  const teamContainerClass = 'team3-container';
  const teamHeaderClass = 'team3-header';
  const teamTitleClass = 'team3-title';
  const teamSubtitleClass = 'team3-subtitle';
  const teamGridClass = 'team3-grid';
  const teamMemberClass = 'team3-member';
  const teamMemberImageContainerClass = 'team3-member-image-container';
  const teamMemberImageClass = 'team3-member-image';
  const teamMemberContentClass = 'team3-member-content';
  const teamMemberNameClass = 'team3-member-name';
  const teamMemberRoleClass = 'team3-member-role';
  const teamMemberBioClass = 'team3-member-bio';
  const teamMemberSocialClass = 'team3-member-social';
  const teamMemberSocialIconClass = 'team3-social-icon';

  // Desktop Styles (default)
  const teamSectionStyles = {
    position: 'relative',
    padding: '100px 20px',
    backgroundColor: '#ffffff', // Lighter background
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflowX: 'hidden'
  };

  const teamContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1100px', // Slightly reduced max width
    width: '100%',
    margin: '0 auto',
    padding: '0 15px',
    gap: '50px' // Increased gap
  };

  const teamHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '750px',
    width: '100%'
  };

  const teamTitleStyles = {
    fontSize: '44px', // Larger title
    fontWeight: 'bold',
    color: '#2d3748', // Darker title color
    marginBottom: '20px',
    fontFamily: "'Montserrat', sans-serif" // Different font
  };

  const teamSubtitleStyles = {
    fontSize: '18px',
    color: '#718096', // Softer subtitle color
    lineHeight: '1.7',
    marginBottom: '30px'
  };

  const teamGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)', // Default to 3 columns
    gap: '30px', // Larger gap
    width: '100%'
  };

  const teamMemberStyles = {
    backgroundColor: '#f7fafc', // Light card background
    borderRadius: '12px', // More rounded corners
    padding: '25px',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    transition: 'transform 0.3s ease, boxShadow 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };

  const teamMemberImageContainerStyles = {
    width: '130px', // Larger image container
    height: '130px',
    borderRadius: '50%',
    overflow: 'hidden',
    marginBottom: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  };

  const teamMemberImageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  };

  const teamMemberContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  };

  const teamMemberNameStyles = {
    fontSize: '22px', // Larger name
    fontWeight: '600',
    color: '#2d3748',
    fontFamily: "'Montserrat', sans-serif"
  };

  const teamMemberRoleStyles = {
    fontSize: '15px', // Slightly larger role
    color: '#4a5568',
    fontWeight: '500',
    marginBottom: '10px'
  };

  const teamMemberBioStyles = {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.6',
    marginBottom: '15px',
    minHeight: '60px' // Ensure some consistent height
  };

  const teamMemberSocialStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px' // Larger gap for social icons
  };

  const teamMemberSocialIconStyles = {
    width: '32px', // Larger social icons
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    color: '#4a5568',
    transition: 'background-color 0.3s ease, color 0.3s ease, transform 0.2s ease',
    cursor: 'pointer'
  };

  // Custom hover styles
  const teamMemberHoverStyles = {
    '&:hover': {
      transform: 'translateY(-8px)', // More pronounced lift
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)'
    }
  };

  const socialIconHoverStyles = {
    '&:hover': {
      backgroundColor: '#3182ce',
      color: '#ffffff',
      transform: 'scale(1.1)'
    }
  };


  // Tablet Styles
  const tabletTeamGridStyles = {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '25px'
  };

  // Mobile Styles
  const mobileTeamGridStyles = {
    gridTemplateColumns: '1fr', // Single column for mobile
    gap: '20px'
  };
  const mobileTeamTitleStyles = {
    fontSize: '36px'
  };
  const mobileTeamMemberStyles = {
    padding: '20px'
  };


  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };

    newRegistry[DEVICES.DESKTOP].set(teamSectionClass, teamSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(teamContainerClass, teamContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(teamHeaderClass, teamHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(teamTitleClass, teamTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamSubtitleClass, teamSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamGridClass, teamGridStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberClass, { ...teamMemberStyles, ...teamMemberHoverStyles });
    newRegistry[DEVICES.DESKTOP].set(teamMemberImageContainerClass, teamMemberImageContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberImageClass, teamMemberImageStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberContentClass, teamMemberContentStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberNameClass, teamMemberNameStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberRoleClass, teamMemberRoleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberBioClass, teamMemberBioStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberSocialClass, teamMemberSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberSocialIconClass, { ...teamMemberSocialIconStyles, ...socialIconHoverStyles });


    newRegistry[DEVICES.TABLET].set(teamGridClass, tabletTeamGridStyles);
    newRegistry[DEVICES.TABLET].set(teamTitleClass, { fontSize: '40px' }); // Adjusted tablet title

    newRegistry[DEVICES.MOBILE].set(teamGridClass, mobileTeamGridStyles);
    newRegistry[DEVICES.MOBILE].set(teamTitleClass, mobileTeamTitleStyles);
    newRegistry[DEVICES.MOBILE].set(teamMemberClass, { ...teamMemberStyles, ...mobileTeamMemberStyles }); // Apply mobile padding
    newRegistry[DEVICES.MOBILE].set(teamSubtitleClass, { fontSize: '16px' });

    return newRegistry;
  });

  setGlobalClassNames(prev => new Set([
    ...prev,
    teamSectionClass, teamContainerClass, teamHeaderClass, teamTitleClass,
    teamSubtitleClass, teamGridClass, teamMemberClass, teamMemberImageContainerClass,
    teamMemberImageClass, teamMemberContentClass, teamMemberNameClass,
    teamMemberRoleClass, teamMemberBioClass, teamMemberSocialClass, teamMemberSocialIconClass
  ]));

  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const gridId = sectionId + 5;

  const teamMembersData = [
    {
      name: 'Elena Rodriguez',
      role: 'Creative Director',
      image: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=150&q=80',
      bio: 'Elena drives the creative vision, ensuring every project is both beautiful and impactful. She loves minimalist art.',
      social: { twitter: '#', linkedin: '#', dribbble: '#' }
    },
    {
      name: 'Kenji Tanaka',
      role: 'Lead Software Architect',
      image: 'https://images.unsplash.com/photo-1530268729831-4b0b9e170218?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=150&q=80',
      bio: 'Kenji is the mastermind behind our robust and scalable platforms. He enjoys coding and exploring new tech.',
      social: { github: '#', linkedin: '#', website: '#' }
    },
    {
      name: 'Aisha Ibrahim',
      role: 'Head of User Experience',
      image: 'https://images.unsplash.com/photo-1488426862026-39b1ac707941?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=150&q=80',
      bio: 'Aisha crafts intuitive and engaging user journeys. She believes in user-first design principles.',
      social: { linkedin: '#', behance: '#', email: '#' }
    }
  ];

  const elements = [
    {
      id: sectionId, builderId: uuidv4(), type: 'section', name: 'Team Section 3', parentId: null,
      className: teamSectionClass, styles: {}, children: [containerId], text: '',
      attributes: { 'data-element': 'team-section', 'data-component': 'section', 'data-section-type': 'team', 'data-page-id': currentPageId }
    },
    {
      id: containerId, builderId: uuidv4(), type: 'div', name: 'Team Container 3', parentId: sectionId,
      hierarchyName: getHierarchyName('div'), className: teamContainerClass, styles: {}, children: [headerId, gridId], text: '',
      attributes: { 'data-element': 'team-container' }
    },
    {
      id: headerId, builderId: uuidv4(), type: 'div', name: 'Team Header 3', parentId: containerId,
      hierarchyName: getHierarchyName('div'), className: teamHeaderClass, styles: {}, children: [titleId, subtitleId], text: '',
      attributes: { 'data-element': 'team-header' }
    },
    {
      id: titleId, builderId: uuidv4(), type: 'h2', name: 'Team Title 3', parentId: headerId,
      hierarchyName: getHierarchyName('h2'), className: teamTitleClass, styles: {}, children: [],
      text: 'Our Innovators & Experts', attributes: { 'data-element': 'team-title' }
    },
    {
      id: subtitleId, builderId: uuidv4(), type: 'p', name: 'Team Subtitle 3', parentId: headerId,
      hierarchyName: getHierarchyName('p'), className: teamSubtitleClass, styles: {}, children: [],
      text: 'Meet the brilliant minds driving our success. Passionate, dedicated, and always pushing boundaries.',
      attributes: { 'data-element': 'team-subtitle' }
    },
    {
      id: gridId, builderId: uuidv4(), type: 'div', name: 'Team Grid 3', parentId: containerId,
      hierarchyName: getHierarchyName('div'), className: teamGridClass, styles: {}, children: [], text: '',
      attributes: { 'data-element': 'team-grid' }
    }
  ];

  let idOffset = 6;
  elements[5].children = []; // Ensure grid children is empty

  teamMembersData.forEach((member, index) => {
    const memberId = sectionId + idOffset++;
    const imageContainerId = sectionId + idOffset++;
    const imageId = sectionId + idOffset++;
    const contentId = sectionId + idOffset++;
    const nameId = sectionId + idOffset++;
    const roleId = sectionId + idOffset++;
    const bioId = sectionId + idOffset++;
    const socialId = sectionId + idOffset++;

    elements[5].children.push(memberId); // Add member to grid's children

    elements.push({
      id: memberId, builderId: uuidv4(), type: 'div', name: `Team Member ${index + 1}`, parentId: gridId,
      hierarchyName: getHierarchyName('div'), className: teamMemberClass, styles: {},
      children: [imageContainerId, contentId], text: '', attributes: { 'data-element': 'team-member' }
    });

    elements.push({
      id: imageContainerId, builderId: uuidv4(), type: 'div', name: `Image Container ${index + 1}`, parentId: memberId,
      hierarchyName: getHierarchyName('div'), className: teamMemberImageContainerClass, styles: {},
      children: [imageId], text: '', attributes: { 'data-element': 'team-member-image-container' }
    });

    elements.push({
      id: imageId, builderId: uuidv4(), type: 'image', name: `Member Image ${index + 1}`, parentId: imageContainerId,
      hierarchyName: getHierarchyName('image'), className: teamMemberImageClass, styles: {}, src: member.image,
      attributes: { 'data-element': 'team-member-image', 'alt': member.name }
    });

    elements.push({
      id: contentId, builderId: uuidv4(), type: 'div', name: `Content ${index + 1}`, parentId: memberId,
      hierarchyName: getHierarchyName('div'), className: teamMemberContentClass, styles: {},
      children: [nameId, roleId, bioId, socialId], text: '', attributes: { 'data-element': 'team-member-content' }
    });

    elements.push({
      id: nameId, builderId: uuidv4(), type: 'h3', name: `Member Name ${index + 1}`, parentId: contentId,
      hierarchyName: getHierarchyName('h3'), className: teamMemberNameClass, styles: {}, children: [],
      text: member.name, attributes: { 'data-element': 'team-member-name' }
    });

    elements.push({
      id: roleId, builderId: uuidv4(), type: 'p', name: `Member Role ${index + 1}`, parentId: contentId,
      hierarchyName: getHierarchyName('p'), className: teamMemberRoleClass, styles: {}, children: [],
      text: member.role, attributes: { 'data-element': 'team-member-role' }
    });

    elements.push({
      id: bioId, builderId: uuidv4(), type: 'p', name: `Member Bio ${index + 1}`, parentId: contentId,
      hierarchyName: getHierarchyName('p'), className: teamMemberBioClass, styles: {}, children: [],
      text: member.bio, attributes: { 'data-element': 'team-member-bio' }
    });

    elements.push({
      id: socialId, builderId: uuidv4(), type: 'div', name: `Social Links ${index + 1}`, parentId: contentId,
      hierarchyName: getHierarchyName('div'), className: teamMemberSocialClass, styles: {}, children: [], text: '',
      attributes: { 'data-element': 'team-member-social' }
    });

    Object.entries(member.social).forEach(([platform, url]) => {
      const socialLinkId = sectionId + idOffset++;
      const socialIconImageId = sectionId + idOffset++;
      elements.find(el => el.id === socialId).children.push(socialLinkId);

      elements.push({
        id: socialLinkId, builderId: uuidv4(), type: 'link', name: `${platform.charAt(0).toUpperCase() + platform.slice(1)} Link`, parentId: socialId,
        hierarchyName: getHierarchyName('link'), className: teamMemberSocialIconClass, styles: {}, children: [socialIconImageId], text: '',
        attributes: { 'data-element': 'social-link', 'href': url, 'target': '_blank', 'rel': 'noopener noreferrer', 'aria-label': platform }
      });
      elements.push({
        id: socialIconImageId, builderId: uuidv4(), type: 'image', name: `${platform.charAt(0).toUpperCase() + platform.slice(1)} Icon`, parentId: socialLinkId,
        hierarchyName: getHierarchyName('image'), styles: { width: '16px', height: '16px' }, // Icon size within the link
        src: `https://api.iconify.design/mdi:${platform.toLowerCase()}.svg?color=currentColor`, // Using currentColor and mdi icons
        attributes: { 'data-element': 'social-icon', 'alt': platform }
      });
    });
  });
  
  // Ensure correct parent-child relationships
  const finalElements = [];
  const elementMap = new Map(elements.map(el => [el.id, { ...el, children: [...(el.children || [])] }])); // Ensure children is always an array

  elements.forEach(el => {
    if (el.parentId === null) {
      // It's a root element, it should already be in finalElements if we build it from the map later
    } else {
      const parent = elementMap.get(el.parentId);
      if (parent) {
        // Check if child already exists to prevent duplicates. Push if not.
        if (!parent.children.includes(el.id)) {
            // This logic for adding children to parent should ideally happen once
            // when the elementMap is being populated or grid children are set.
            // However, to be safe, we ensure it here.
            // parent.children.push(el.id); 
            // Deferring direct manipulation of parent.children here to avoid out-of-order issues
            // The original element.children arrays should be the source of truth for order.
        }
      }
    }
  });
  
  // Re-populate children arrays based on original definitions to maintain order
  elements.forEach(originalEl => {
      const mappedEl = elementMap.get(originalEl.id);
      if (mappedEl && originalEl.children && originalEl.children.length > 0) {
          // Filter out any undefined children that might have resulted from id issues
          mappedEl.children = originalEl.children.filter(childId => elementMap.has(childId));
      }
  });

  // Reconstruct the elements array from the map to ensure all elements are included
  // and parent-child relationships from the map are used.
  let reconstructedElements = Array.from(elementMap.values());
  
  // Ensure the grid has the correct children in the correct order
    const gridElementOriginal = elements.find(el => el.id === gridId);
    const gridElementInMap = elementMap.get(gridId);

    if (gridElementOriginal && gridElementInMap) {
        gridElementInMap.children = [...gridElementOriginal.children]; // Use original children order
    }

  // Final pass to ensure all elements in the map are linked correctly
  // This is more about structuring the output `reconstructedElements` correctly
  // based on the `elementMap` which should now have correctly ordered children.
  
  // The `finalElements` array for root nodes might not be necessary if `reconstructedElements` is built carefully.
  // We will return `reconstructedElements` which is `Array.from(elementMap.values())`
  // The parent-child relationships are within the `elementMap` objects themselves.

  return {
    teamSection: elementMap.get(sectionId), // Get the root section element
    elements: reconstructedElements // Return all elements, structured by the map
  };
};

export default createTeam3Elements; 